import { useState, useEffect } from 'react';
import { useI18n } from '../../../../../i18n';
import { useCurrentUser } from '../../../../../hooks/useCurrentUser';
import {
  fetchProyectos,
  fetchMotivosCambioEstado,
  fetchHistorialEstadoPersona,
  fetchPreviewCodigoPersona,
  postCambioEstadoPersona,
  request,
  type PersonaDetalle,
  type MotivoCambioEstado,
  type HistorialEstadoItem,
} from '../../../../shared/services/api';
import {
  cardStyle,
  labelStyle,
  inputStyle,
  gridStyle2,
  gridStyle3,
  type SelectOpt,
} from './persona-detail-shared';
import { C } from '../../../../../lib/design';

function SectionTitle({ title }: { title: string }) {
  return (
    <h3
      style={{
        margin: '20px 0 10px',
        fontSize: 11,
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        color: C.azul1,
        borderBottom: '1px solid #E5E7EB',
        paddingBottom: 6,
      }}
    >
      {title}
    </h3>
  );
}

export function PersonaDatosTab({
  data,
  onSave,
}: {
  data: PersonaDetalle;
  onSave: (updated: Record<string, unknown>) => Promise<void>;
}) {
  const { t } = useI18n();
  // La persona tiene muchos campos opcionales y dinamicos; mantenemos acceso permisivo porque
  // el tipado estricto se aplica al cargar t.* (i18n).
  const p = data.persona as unknown as Record<string, unknown>;
  const admRoot = t.structure.admin;
  const adm = admRoot.sections;
  const pf = t.people.form;
  const ph = t.people.historialEstado;
  const { isAdmin } = useCurrentUser();

  const centroPrincipal =
    data.centros?.find((c) => c.esPrincipal && !c.fechaFin) ??
    data.centros?.find((c) => !c.fechaFin) ??
    null;
  const centroNombre = centroPrincipal?.centro.nombre ?? '—';

  const [form, setForm] = useState<Record<string, string>>(() => ({
    codigo: (p.codigo as string) ?? '',
    nif: (p.nif as string) ?? '',
    nombre: (p.nombre as string) ?? '',
    apellidos: (p.apellidos as string) ?? '',
    nombreMostrar: (p.nombreMostrar as string) ?? '',
    email: (p.email as string) ?? '',
    emailSecundario: (p.emailSecundario as string) ?? '',
    telefono: (p.telefono as string) ?? '',
    telefonoMovil: (p.telefonoMovil as string) ?? '',
    tipoPersona: (p.tipoPersona as string) ?? 'EMPLEADO',
    estado: (p.estado as string) ?? 'ACTIVO',
    tipoContratoId: (p.tipoContratoId as string) ?? '',
    categoriaLaboralId: (p.categoriaLaboralId as string) ?? '',
    departamentoId: (p.departamentoId as string) ?? '',
    // codigoDireccion queda en BD sin editarse desde aqui (deprecado en UI, ver T-5.4 prompt 0450).
    tipoCosto: (p.tipoCosto as string) ?? '',
    centroCostesId: (p.centroCostesId as string) ?? '',
    fechaAlta: ((p.fechaAlta as string) ?? '').slice(0, 10),
    fechaBaja: ((p.fechaBaja as string) ?? '').slice(0, 10),
    fechaNacimiento: ((p.fechaNacimiento as string) ?? '').slice(0, 10),
    paisResidenciaId: (p.paisResidenciaId as string) ?? '',
    generoId: (p.generoId as string) ?? '',
    observaciones: (p.observaciones as string) ?? '',
  }));
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  // Lookups (org/supervisor/centro/pais ya no se editan aqui → derivados)
  const [generos, setGeneros] = useState<SelectOpt[]>([]);
  const [motivos, setMotivos] = useState<MotivoCambioEstado[]>([]);
  const [proyectos, setProyectos] = useState<SelectOpt[]>([]);
  const [tiposContrato, setTiposContrato] = useState<SelectOpt[]>([]);
  const [categoriasLaborales, setCategoriasLaborales] = useState<SelectOpt[]>([]);
  const [departamentos, setDepartamentos] = useState<SelectOpt[]>([]);

  // Cambio de estado
  const estadoActual = (p.estado as string) ?? 'ACTIVO';
  const personaId = p.id as string;
  // Persona nueva = sin id persistido en BD. En el flujo actual del admin
  // siempre es existente; el flag queda listo por si futuros entrypoints
  // usan PersonaDatosTab en modo alta.
  const isNuevo = !personaId;
  const [pendingEstado, setPendingEstado] = useState<string | null>(null);
  const [cambioMotivoId, setCambioMotivoId] = useState('');
  const [cambioFecha, setCambioFecha] = useState(() => new Date().toISOString().slice(0, 10));
  const [cambioObs, setCambioObs] = useState('');
  const [cambioSaving, setCambioSaving] = useState(false);
  const [cambioError, setCambioError] = useState('');
  const [historial, setHistorial] = useState<HistorialEstadoItem[]>([]);
  const [historialLoading, setHistorialLoading] = useState(false);

  useEffect(() => {
    request<{ data: Array<{ id: string; nombre: string }> }>('/catalogos-tenant?tipo=GENERO')
      .then((r) => setGeneros((r.data ?? []).map((g) => ({ value: g.id, label: g.nombre }))))
      .catch(() => {});
    fetchMotivosCambioEstado()
      .then((r) => setMotivos(r.data ?? []))
      .catch(() => {});
    // Solo proyectos activos (selector de Centro de costes)
    fetchProyectos({ porPagina: '100', estado: 'ACTIVO' })
      .then((r) =>
        setProyectos(
          (r.data ?? []).map((pr) => ({
            value: pr.id,
            label: `${pr.codigo} — ${pr.nombre}`,
          })),
        ),
      )
      .catch(() => {});
    request<{ data: Array<{ id: string; nombre: string }> }>('/tipos-contrato')
      .then((r) =>
        setTiposContrato((r.data ?? []).map((tc) => ({ value: tc.id, label: tc.nombre }))),
      )
      .catch(() => {});
    request<{ data: Array<{ id: string; nombre: string }> }>('/categorias-laborales')
      .then((r) =>
        setCategoriasLaborales((r.data ?? []).map((cl) => ({ value: cl.id, label: cl.nombre }))),
      )
      .catch(() => {});
    request<{ data: Array<{ id: string; nombre: string }> }>('/departamentos')
      .then((r) => setDepartamentos((r.data ?? []).map((d) => ({ value: d.id, label: d.nombre }))))
      .catch(() => {});
  }, []);

  // Preview del proximo codigo en modo alta — se recalcula al cambiar tipoPersona.
  // Silencioso si falla: mantiene el valor previo del input.
  useEffect(() => {
    if (!isNuevo) return;
    const tipo = (form.tipoPersona ?? 'EMPLEADO') as 'EMPLEADO' | 'COLABORADOR' | 'EXTERNO';
    let cancelled = false;
    fetchPreviewCodigoPersona(tipo)
      .then((preview) => {
        if (cancelled) return;
        setForm((prev) => ({ ...prev, codigo: preview.proximoCodigo }));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [isNuevo, form.tipoPersona]);

  const loadHistorial = () => {
    if (!personaId) return;
    setHistorialLoading(true);
    fetchHistorialEstadoPersona(personaId)
      .then((r) => setHistorial(r.data ?? []))
      .catch(() => setHistorial([]))
      .finally(() => setHistorialLoading(false));
  };

  useEffect(() => {
    loadHistorial();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personaId]);

  const handleEstadoChange = (nuevo: string) => {
    if (!isAdmin) return;
    if (nuevo === estadoActual) {
      setPendingEstado(null);
      setCambioError('');
      return;
    }
    setPendingEstado(nuevo);
    setCambioMotivoId('');
    setCambioFecha(new Date().toISOString().slice(0, 10));
    setCambioObs('');
    setCambioError('');
  };

  const handleCambioCancelar = () => {
    setPendingEstado(null);
    setCambioError('');
  };

  const handleCambioConfirmar = async () => {
    if (!pendingEstado || !personaId) return;
    if (!cambioFecha) {
      setCambioError(t.people.validation.required);
      return;
    }
    if (['BAJA', 'SUSPENDIDO'].includes(pendingEstado) && !cambioMotivoId) {
      setCambioError(pf.cambioEstadoMotivoBaja);
      return;
    }
    setCambioSaving(true);
    setCambioError('');
    try {
      await postCambioEstadoPersona(personaId, {
        estadoNuevo: pendingEstado as 'ACTIVO' | 'BAJA' | 'SUSPENDIDO',
        motivoId: cambioMotivoId || undefined,
        fechaCambio: cambioFecha,
        observaciones: cambioObs || undefined,
      });
      set('estado', pendingEstado);
      if (pendingEstado === 'BAJA') set('fechaBaja', cambioFecha);
      setPendingEstado(null);
      loadHistorial();
      setMsg(t.people.savedOk);
      setTimeout(() => setMsg(''), 2000);
    } catch (err: unknown) {
      setCambioError(err instanceof Error ? err.message : 'Error');
    } finally {
      setCambioSaving(false);
    }
  };

  // Org/Depto/Supervisor/Centro son derivados del puesto/centros (solo lectura)
  const set = (key: string, value: string) => setForm((prev) => ({ ...prev, [key]: value }));

  // Validation
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const validate = (): Record<string, string> => {
    const errs: Record<string, string> = {};
    const req = t.people.validation.required;
    // codigo es autogenerado — no validar
    if (!form.nombre.trim()) errs.nombre = req;
    if (!form.apellidos.trim()) errs.apellidos = req;
    if (!form.email.trim()) errs.email = req;
    else if (!EMAIL_RE.test(form.email)) errs.email = t.people.validation.emailFormat;
    return errs;
  };

  const handleSave = async () => {
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs);
      return;
    }
    setFieldErrors({});
    setSaving(true);
    try {
      const body: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(form)) {
        // codigo es fuente de verdad backend (autogenerado)
        if (k === 'codigo') continue;
        if (v !== '') body[k] = v;
      }
      await onSave(body);
      setMsg(t.people.savedOk);
      setTimeout(() => setMsg(''), 2000);
    } catch (err: unknown) {
      setMsg(`Error: ${err instanceof Error ? err.message : 'desconocido'}`);
    } finally {
      setSaving(false);
    }
  };

  const errStyle: React.CSSProperties = { fontSize: 11, color: '#DC2626', marginTop: 2 };
  const inputErrStyle: React.CSSProperties = { ...inputStyle, borderColor: '#DC2626' };

  const renderSelect = (value: string, options: SelectOpt[], onChange: (v: string) => void) => (
    <select
      style={{ ...inputStyle, background: '#fff' }}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    >
      <option value="">--</option>
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );

  return (
    <div style={cardStyle}>
      {/* Seccion 1: Identificacion */}
      <SectionTitle title={adm?.identification ?? 'Identificacion'} />
      <div style={gridStyle2}>
        <div>
          <label style={labelStyle}>
            {t.people.form.code}{' '}
            <span style={{ fontSize: 10, color: '#9CA3AF' }}>
              {isNuevo ? '(preview)' : pf.codigoAutogenerado}
            </span>
          </label>
          <input
            style={{ ...inputStyle, background: '#F8FAFC', cursor: 'default' }}
            value={form.codigo || '—'}
            readOnly
          />
        </div>
        <div>
          <label style={labelStyle}>{t.people.form.nif}</label>
          <input style={inputStyle} value={form.nif} onChange={(e) => set('nif', e.target.value)} />
        </div>
        <div>
          <label style={labelStyle}>{t.people.form.firstName}</label>
          <input
            style={fieldErrors.nombre ? inputErrStyle : inputStyle}
            value={form.nombre}
            onChange={(e) => set('nombre', e.target.value)}
          />
          {fieldErrors.nombre && <div style={errStyle}>{fieldErrors.nombre}</div>}
        </div>
        <div>
          <label style={labelStyle}>{t.people.form.lastName}</label>
          <input
            style={fieldErrors.apellidos ? inputErrStyle : inputStyle}
            value={form.apellidos}
            onChange={(e) => set('apellidos', e.target.value)}
          />
          {fieldErrors.apellidos && <div style={errStyle}>{fieldErrors.apellidos}</div>}
        </div>
        <div style={{ gridColumn: '1 / -1' }}>
          <label style={labelStyle}>{t.people.form.displayName}</label>
          <input
            style={inputStyle}
            value={form.nombreMostrar}
            onChange={(e) => set('nombreMostrar', e.target.value)}
          />
        </div>
      </div>

      {/* Seccion 2: Contacto */}
      <SectionTitle title={adm?.contact ?? 'Contacto'} />
      <div style={gridStyle2}>
        <div>
          <label style={labelStyle}>{t.people.form.email}</label>
          <input
            type="email"
            style={fieldErrors.email ? inputErrStyle : inputStyle}
            value={form.email}
            onChange={(e) => set('email', e.target.value)}
          />
          {fieldErrors.email && <div style={errStyle}>{fieldErrors.email}</div>}
        </div>
        <div>
          <label style={labelStyle}>{t.people.form.secondaryEmail}</label>
          <input
            type="email"
            style={inputStyle}
            value={form.emailSecundario}
            onChange={(e) => set('emailSecundario', e.target.value)}
          />
        </div>
        <div>
          <label style={labelStyle}>{t.people.form.phone}</label>
          <input
            style={inputStyle}
            value={form.telefono}
            onChange={(e) => set('telefono', e.target.value)}
          />
        </div>
        <div>
          <label style={labelStyle}>{t.people.form.mobilePhone}</label>
          <input
            style={inputStyle}
            value={form.telefonoMovil}
            onChange={(e) => set('telefonoMovil', e.target.value)}
          />
        </div>
      </div>

      {/* Seccion 3: Datos laborales */}
      <SectionTitle title={adm?.employment ?? 'Datos laborales'} />
      <div style={gridStyle3}>
        <div>
          <label style={labelStyle}>{t.people.form.personType}</label>
          <select
            style={{ ...inputStyle, background: '#fff' }}
            value={form.tipoPersona}
            onChange={(e) => set('tipoPersona', e.target.value)}
          >
            <option value="EMPLEADO">{admRoot.tipoPersonaEmpleado}</option>
            <option value="COLABORADOR">{admRoot.tipoPersonaColaborador}</option>
            <option value="EXTERNO">{admRoot.tipoPersonaExterno}</option>
          </select>
        </div>
        <div>
          <label style={labelStyle}>
            {t.people.form.status}{' '}
            {!isAdmin && (
              <span style={{ fontSize: 10, color: '#9CA3AF' }}>{t.people.form.readOnly}</span>
            )}
          </label>
          <select
            style={{ ...inputStyle, background: isAdmin ? '#fff' : '#F3F4F6' }}
            value={pendingEstado ?? form.estado}
            onChange={(e) => handleEstadoChange(e.target.value)}
            disabled={!isAdmin}
          >
            <option value="ACTIVO">{admRoot.estadoActivo}</option>
            <option value="BAJA">{admRoot.estadoBaja}</option>
            <option value="SUSPENDIDO">{admRoot.estadoSuspendido}</option>
          </select>
        </div>
        <div>
          <label style={labelStyle}>{t.people.form.contractType}</label>
          {renderSelect(form.tipoContratoId, tiposContrato, (v) => set('tipoContratoId', v))}
        </div>
        <div>
          <label style={labelStyle}>{t.people.form.jobCategory}</label>
          {renderSelect(form.categoriaLaboralId, categoriasLaborales, (v) =>
            set('categoriaLaboralId', v),
          )}
        </div>
        <div>
          <label style={labelStyle}>{t.people.form.hireDate}</label>
          <input
            type="date"
            style={inputStyle}
            value={form.fechaAlta}
            onChange={(e) => set('fechaAlta', e.target.value)}
          />
        </div>
        <div>
          <label style={labelStyle}>{t.people.form.terminationDate}</label>
          <input
            type="date"
            style={inputStyle}
            value={form.fechaBaja}
            onChange={(e) => set('fechaBaja', e.target.value)}
          />
        </div>
        <div>
          <label style={labelStyle}>{t.people.form.birthDate}</label>
          <input
            type="date"
            style={inputStyle}
            value={form.fechaNacimiento}
            onChange={(e) => set('fechaNacimiento', e.target.value)}
          />
        </div>
        <div>
          <label style={labelStyle}>{pf.tipoCosto}</label>
          <select
            style={{ ...inputStyle, background: '#fff' }}
            value={form.tipoCosto}
            onChange={(e) => set('tipoCosto', e.target.value)}
          >
            <option value="">{t.common.sinSeleccion}</option>
            <option value="OPERACIONES">{admRoot.tipoCostoOperaciones}</option>
            <option value="ESTRUCTURA_PROYECTO">{admRoot.tipoCostoEstructuraProyecto}</option>
            <option value="ESTRUCTURA_CORPORATIVA">{admRoot.tipoCostoEstructuraCorporativa}</option>
          </select>
        </div>
        <div>
          <label style={labelStyle}>{pf.centroCostes}</label>
          {renderSelect(form.centroCostesId, proyectos, (v) => set('centroCostesId', v))}
        </div>
      </div>

      {/* Mini-form cambio de estado */}
      {pendingEstado && (
        <div
          style={{
            marginTop: 12,
            padding: 12,
            border: `1px solid ${C.azul2}`,
            borderRadius: 8,
            background: '#F8FAFC',
          }}
        >
          <div style={{ fontSize: 12, fontWeight: 700, color: C.azul1, marginBottom: 8 }}>
            {pf.cambioEstadoTitulo(estadoActual, pendingEstado)}
          </div>
          <div style={gridStyle3}>
            <div>
              <label style={labelStyle}>{pf.cambioEstadoMotivo}</label>
              <select
                style={{ ...inputStyle, background: '#fff' }}
                value={cambioMotivoId}
                onChange={(e) => setCambioMotivoId(e.target.value)}
              >
                <option value="">{t.common.sinSeleccion}</option>
                {motivos.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.nombre}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label style={labelStyle}>{pf.cambioEstadoFecha}</label>
              <input
                type="date"
                style={inputStyle}
                value={cambioFecha}
                onChange={(e) => setCambioFecha(e.target.value)}
              />
            </div>
            <div>
              <label style={labelStyle}>{pf.cambioEstadoObs}</label>
              <input
                style={inputStyle}
                value={cambioObs}
                onChange={(e) => setCambioObs(e.target.value)}
                placeholder={pf.cambioEstadoObsPlaceholder}
              />
            </div>
          </div>
          {cambioError && (
            <div style={{ fontSize: 12, color: '#DC2626', marginTop: 8 }}>{cambioError}</div>
          )}
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <button
              onClick={handleCambioConfirmar}
              disabled={cambioSaving}
              style={{
                padding: '6px 14px',
                border: 'none',
                borderRadius: 6,
                background: cambioSaving ? '#9CA3AF' : C.azul2,
                color: '#fff',
                cursor: cambioSaving ? 'not-allowed' : 'pointer',
                fontSize: 12,
                fontWeight: 600,
                fontFamily: 'inherit',
              }}
            >
              {cambioSaving ? t.common.guardando : pf.cambioEstadoConfirmar}
            </button>
            <button
              onClick={handleCambioCancelar}
              disabled={cambioSaving}
              style={{
                padding: '6px 14px',
                border: '1px solid #D1D5DB',
                borderRadius: 6,
                background: '#fff',
                color: C.text,
                cursor: cambioSaving ? 'not-allowed' : 'pointer',
                fontSize: 12,
                fontFamily: 'inherit',
              }}
            >
              {t.common.cancelar}
            </button>
          </div>
        </div>
      )}

      {/* Seccion 4: Organizacion (org/supervisor/pais derivados; departamento editable) */}
      <SectionTitle title={adm?.organization ?? 'Organizacion'} />
      <div style={gridStyle3}>
        <div>
          <label style={labelStyle}>{t.structure.organizacionLabel}</label>
          <div
            style={{
              ...inputStyle,
              background: '#F8FAFC',
              color: '#64748B',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'default',
            }}
          >
            <span>{data.puestoActual?.organizacion?.nombre ?? '—'}</span>
            <span style={{ fontSize: 10, color: '#94A3B8', fontStyle: 'italic' }}>
              {pf.readOnly}
            </span>
          </div>
        </div>
        <div>
          <label style={labelStyle}>{t.people.form.department}</label>
          {renderSelect(form.departamentoId, departamentos, (v) => set('departamentoId', v))}
        </div>
        <div>
          <label style={labelStyle}>{pf.center ?? pf.workCenter}</label>
          <div
            style={{
              ...inputStyle,
              background: '#F8FAFC',
              color: '#64748B',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'default',
            }}
          >
            <span>{centroNombre}</span>
            <span style={{ fontSize: 10, color: '#94A3B8', fontStyle: 'italic' }}>
              {`→ ${t.people.centers}`}
            </span>
          </div>
        </div>
        <div>
          <label style={labelStyle}>{t.people.form.supervisor}</label>
          <div
            style={{
              ...inputStyle,
              background: '#F8FAFC',
              color: '#64748B',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              justifyContent: 'center',
              cursor: 'default',
              minHeight: 48,
              padding: '6px 10px',
            }}
          >
            {data.supervisorDerivado ? (
              <>
                <span style={{ fontWeight: 600, color: C.azul1 }}>
                  {data.supervisorDerivado.nombre} {data.supervisorDerivado.apellidos}
                </span>
                <span style={{ fontSize: 10, color: '#94A3B8' }}>
                  {data.supervisorDerivado.codigoPuesto ?? '—'} —{' '}
                  {data.supervisorDerivado.tituloPuesto}
                </span>
              </>
            ) : (
              <span>—</span>
            )}
          </div>
        </div>
        <div>
          <label style={labelStyle}>{t.people.form.country}</label>
          <div
            title={pf.paisDerivadoTooltip}
            style={{
              ...inputStyle,
              background: '#F8FAFC',
              color: '#64748B',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'default',
            }}
          >
            <span>{data.paisCentroPrincipal?.nombre ?? '—'}</span>
            <span style={{ fontSize: 10, color: '#94A3B8', fontStyle: 'italic' }}>
              {pf.readOnly}
            </span>
          </div>
        </div>
        <div>
          <label style={labelStyle}>{t.people.form.gender}</label>
          {renderSelect(form.generoId, generos, (v) => set('generoId', v))}
        </div>
      </div>

      {/* Seccion 5: Observaciones */}
      <SectionTitle title={adm?.notes ?? 'Observaciones'} />
      <div style={{ marginBottom: 12 }}>
        <textarea
          style={{ ...inputStyle, resize: 'vertical', minHeight: 60 }}
          rows={3}
          value={form.observaciones}
          onChange={(e) => set('observaciones', e.target.value)}
        />
      </div>

      {/* Historial de cambios de estado */}
      <SectionTitle title={ph?.titulo ?? 'Historial de estado'} />
      {historialLoading ? (
        <div style={{ fontSize: 12, color: '#6B7280' }}>{t.common.cargando}</div>
      ) : historial.length === 0 ? (
        <div style={{ fontSize: 12, color: '#6B7280', fontStyle: 'italic' }}>
          {ph?.sinCambios ?? 'Sin cambios registrados.'}
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: 12,
              fontFamily: 'inherit',
            }}
          >
            <thead>
              <tr style={{ background: '#F3F4F6', textAlign: 'left' }}>
                <th style={{ padding: '6px 8px', borderBottom: '1px solid #E5E7EB' }}>
                  {ph?.colFecha ?? 'Fecha'}
                </th>
                <th style={{ padding: '6px 8px', borderBottom: '1px solid #E5E7EB' }}>
                  {ph?.colDe ?? 'De'}
                </th>
                <th style={{ padding: '6px 8px', borderBottom: '1px solid #E5E7EB' }}>
                  {ph?.colA ?? 'A'}
                </th>
                <th style={{ padding: '6px 8px', borderBottom: '1px solid #E5E7EB' }}>
                  {ph?.colMotivo ?? 'Motivo'}
                </th>
                <th style={{ padding: '6px 8px', borderBottom: '1px solid #E5E7EB' }}>
                  {ph?.colObservaciones ?? 'Observaciones'}
                </th>
                <th style={{ padding: '6px 8px', borderBottom: '1px solid #E5E7EB' }}>
                  {ph?.colPor ?? 'Por'}
                </th>
              </tr>
            </thead>
            <tbody>
              {historial.map((h) => (
                <tr key={h.id} style={{ borderBottom: '1px solid #F3F4F6' }}>
                  <td style={{ padding: '6px 8px' }}>{h.fechaCambio.slice(0, 10)}</td>
                  <td style={{ padding: '6px 8px', color: '#6B7280' }}>{h.estadoAnterior}</td>
                  <td style={{ padding: '6px 8px', fontWeight: 600, color: C.azul1 }}>
                    {h.estadoNuevo}
                  </td>
                  <td style={{ padding: '6px 8px' }}>{h.motivo?.nombre ?? '—'}</td>
                  <td style={{ padding: '6px 8px', color: '#4B5563' }}>{h.observaciones ?? '—'}</td>
                  <td style={{ padding: '6px 8px', color: '#6B7280' }}>
                    {h.cambiadoPor?.nombreCompleto ?? '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Actions */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 16 }}>
        <button
          onClick={handleSave}
          disabled={saving || Object.keys(fieldErrors).length > 0}
          style={{
            padding: '8px 20px',
            border: 'none',
            borderRadius: 6,
            background: saving || Object.keys(fieldErrors).length > 0 ? '#9CA3AF' : C.azul2,
            color: '#fff',
            cursor: saving || Object.keys(fieldErrors).length > 0 ? 'not-allowed' : 'pointer',
            fontSize: 13,
            fontWeight: 600,
            fontFamily: 'inherit',
          }}
        >
          {saving ? t.common.guardando : t.people.saveChanges}
        </button>
        {msg && (
          <span style={{ fontSize: 12, color: msg.startsWith('Error') ? '#DC2626' : '#16A34A' }}>
            {msg}
          </span>
        )}
      </div>
    </div>
  );
}
